import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PdfUploader, type UploadPhase } from "../components/PdfUploader";
import { FieldToggle, type FieldMode } from "../components/FieldToggle";
import {
  uploadDocument,
  UploadError,
  createSession,
  getSession,
  getDocument,
  RECOGNITION_TYPES,
  type UploadResult,
  type RecognitionType,
  type EnrichedField,
} from "../api/client";

interface FieldState {
  mode: FieldMode;
  value: string;
}

const STEPS = [
  { label: "Riconoscimento", icon: "shield" },
  { label: "Documento", icon: "upload_file" },
  { label: "Destinatario", icon: "person" },
  { label: "Riepilogo", icon: "checklist" },
] as const;

/* ─── Stepper bar ─── */
function Stepper({ current }: { current: number }) {
  return (
    <div className="wizard-stepper">
      {STEPS.map((s, i) => {
        const cls = i < current ? "done" : i === current ? "active" : "";
        return (
          <div key={i} className={`wizard-step ${cls}`}>
            <div className="wizard-step-circle">
              {i < current ? (
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>check</span>
              ) : (
                i + 1
              )}
            </div>
            <span className="wizard-step-label">{s.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export function DocumentUpload() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const retrySessionId = searchParams.get("retry");

  const [step, setStep] = useState(0);
  const [isRetryMode, setIsRetryMode] = useState(false);
  const [retryLoading, setRetryLoading] = useState(!!retrySessionId);

  // Step 0 — Recognition type
  const [recogType, setRecogType] = useState<RecognitionType | "">("");

  // Step 1 — Document upload
  const [uploading, setUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>("idle");
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [fields, setFields] = useState<Record<string, FieldState>>({});

  // Step 2 — Recipient
  const [recipientName, setRecipientName] = useState("");
  const [recipientSurname, setRecipientSurname] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [companyName, setCompanyName] = useState("");

  // Global
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");

  // Retry mode: load session + document data and jump to step 3
  useEffect(() => {
    if (!retrySessionId) return;
    (async () => {
      try {
        const session = await getSession(retrySessionId);
        const doc = await getDocument(session.document_id);

        // Reconstruct UploadResult from document data
        const docFields = Array.isArray(doc.acroform_fields) ? doc.acroform_fields : [];
        const sigFields: string[] = doc.signature_fields || (doc.signature_field_name ? [doc.signature_field_name] : []);
        const sigSet = new Set(sigFields);
        setUploadResult({
          document_id: doc.id,
          filename: doc.filename,
          fields: docFields,
          enriched_fields: doc.enriched_fields || null,
          field_count: docFields.filter((f: { name: string }) => !sigSet.has(f.name)).length,
          pages: 0,
          signature_field_name: sigFields[0] || null,
          signature_fields: sigFields,
        });

        // Reconstruct field states from session
        const fieldState: Record<string, FieldState> = {};
        const prefilled = session.prefilled_data || {};
        const toCollect = session.fields_to_collect || [];
        for (const f of docFields) {
          if (toCollect.includes(f.name)) {
            fieldState[f.name] = { mode: "agent", value: "" };
          } else if (f.name in prefilled) {
            fieldState[f.name] = { mode: "prefill", value: prefilled[f.name] };
          } else {
            fieldState[f.name] = { mode: "agent", value: "" };
          }
        }
        setFields(fieldState);

        // Set recognition type
        if (session.recognition_type) {
          setRecogType(session.recognition_type as RecognitionType);
        }

        // Set recipient data
        const fullName = session.recipient_name || "";
        const parts = fullName.split(" ");
        setRecipientName(parts[0] || "");
        setRecipientSurname(parts.slice(1).join(" ") || "");
        setRecipientPhone(session.recipient_phone || "");
        setCompanyName(session.company_name || "");

        setIsRetryMode(true);
        setStep(3); // Jump to Riepilogo
      } catch (e) {
        setError("Impossibile caricare la sessione: " + (e as Error).message);
      } finally {
        setRetryLoading(false);
      }
    })();
  }, [retrySessionId]);

  /* ── handlers ── */
  const handleUpload = async (file: File) => {
    setError("");
    setUploadResult(null);
    setUploading(true);
    setUploadPhase("uploading");

    // Simulate phase transitions (the API call is single request,
    // but we show progress to keep the user informed)
    const phaseTimer1 = setTimeout(() => setUploadPhase("validating"), 1500);
    const phaseTimer2 = setTimeout(() => setUploadPhase("analyzing"), 3500);

    try {
      const result = await uploadDocument(file);
      clearTimeout(phaseTimer1);
      clearTimeout(phaseTimer2);
      setUploadPhase("done");
      // Adjust field_count to exclude all signature widgets — they are not data fields
      const sigSet = new Set(result.signature_fields || (result.signature_field_name ? [result.signature_field_name] : []));
      if (sigSet.size > 0) {
        result.field_count = result.fields.filter((f) => !sigSet.has(f.name)).length;
      }
      setUploadResult(result);
      const initial: Record<string, FieldState> = {};
      for (const f of result.fields) {
        if (!sigSet.has(f.name)) {
          initial[f.name] = { mode: "agent", value: "" };
        }
      }
      setFields(initial);
    } catch (e) {
      clearTimeout(phaseTimer1);
      clearTimeout(phaseTimer2);
      setUploadPhase("idle");
      if (e instanceof UploadError && e.code === "no_acroform") {
        setError(
          "⚠️ Il PDF non contiene campi compilabili (modulo AcroForm). " +
            "Per usare TrustyCERT il documento deve avere campi compilabili (testo, data, " +
            "checkbox, firma digitale). Aprilo con Adobe Acrobat → Strumenti → Prepara modulo, " +
            "aggiungi i campi necessari (incluso almeno un campo Firma digitale) e ricaricalo."
        );
      } else if (e instanceof UploadError && e.code === "missing_signature_field") {
        setError(
          "⚠️ Il PDF ha campi compilabili ma manca il campo di firma digitale. " +
            "Per essere firmato con InfoCert TOP, il documento deve avere almeno un campo " +
            "di tipo 'firma' (Signature). Aprilo con Adobe Acrobat → Strumenti → Prepara modulo, " +
            "aggiungi un campo Firma digitale e ricaricalo."
        );
      } else {
        setError((e as Error).message);
      }
    } finally {
      setUploading(false);
    }
  };

  const updateField = (name: string, update: Partial<FieldState>) => {
    setFields((prev) => ({
      ...prev,
      [name]: { ...prev[name], ...update },
    }));
  };

  const handleSubmit = async () => {
    if (!uploadResult || !recogType) return;
    setError("");
    setSubmitting(true);

    const prefilled: Record<string, string> = {};
    const toCollect: string[] = [];
    for (const [name, state] of Object.entries(fields)) {
      if (state.mode === "prefill") {
        prefilled[name] = state.value;
      } else {
        toCollect.push(name);
      }
    }

    try {
      if (isRetryMode && retrySessionId) {
        // Delete old session before creating a fresh one
        const { deleteSession } = await import("../api/client");
        try { await deleteSession(retrySessionId); } catch { /* ignore if already gone */ }
      }
      const result = await createSession({
        document_id: uploadResult.document_id,
        recognition_type: recogType,
        recipient: {
          name: recipientName,
          surname: recipientSurname,
          phone: recipientPhone,
        },
        prefilled_data: prefilled,
        fields_to_collect: toCollect,
        company_name: companyName || undefined,
      });
      setSuccess(isRetryMode ? "Sessione riavviata!" : `Sessione creata: ${result.session_id}`);
      setTimeout(() => navigate(`/sessions/${result.session_id}`), 1500);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ── step validation ── */
  const canNext = (): boolean => {
    switch (step) {
      case 0: return recogType !== "";
      case 1: return uploadResult !== null;
      case 2: return recipientName.trim() !== "" && recipientPhone.trim() !== "";
      default: return true;
    }
  };

  const next = () => { if (canNext() && step < 3) setStep(step + 1); };
  const back = () => { if (step > 0) setStep(step - 1); };

  /* ── Step 0 — Recognition type ── */
  const renderRecognition = () => (
    <div className="card">
      <h2 className="section-title">Tipo di Riconoscimento</h2>
      <p style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)", marginBottom: "var(--space-4)" }}>
        Scegli come il firmatario verrà identificato prima della firma.
      </p>
      <div className="recog-grid">
        {RECOGNITION_TYPES.map((rt) => (
          <div
            key={rt.value}
            className={`recog-card${recogType === rt.value ? " selected" : ""}`}
            onClick={() => setRecogType(rt.value)}
          >
            <div className="recog-card-icon">
              <span className="material-symbols-outlined" style={{ fontSize: 36 }}>{rt.icon}</span>
            </div>
            <div className="recog-card-title">{rt.label}</div>
            <div className="recog-card-desc">{rt.description}</div>
          </div>
        ))}
      </div>
    </div>
  );

  /* ── Step 1 — Document ── */
  const renderDocument = () => (
    <>
      {!uploadResult ? (
        <div className="card">
          <h2 className="section-title">Carica Documento</h2>
          <p style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)", marginBottom: "var(--space-4)" }}>
            Carica il template PDF con campi compilabili (AcroForm).
          </p>
          <PdfUploader onFile={handleUpload} isLoading={uploading} uploadPhase={uploadPhase} />
        </div>
      ) : (
        <>
          <div className="card">
            <div className="card-header" style={{ marginBottom: "var(--space-3)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", minWidth: 0 }}>
                <span
                  className="material-symbols-outlined"
                  style={{ color: "var(--indaco-success)", fontSize: 22, flexShrink: 0 }}
                >
                  check_circle
                </span>
                <strong style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {uploadResult.filename}
                </strong>
              </div>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => { setUploadResult(null); setFields({}); }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>swap_horiz</span>
                Cambia documento
              </button>
            </div>
            <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "center" }}>
              <span className="badge badge-info">
                {uploadResult.field_count} campi · {uploadResult.pages} pag.
              </span>
              {uploadResult.signature_fields.length > 0 && (
                <span className="badge badge-success" style={{ gap: 6 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 14, lineHeight: 1 }}>
                    verified
                  </span>
                  {uploadResult.signature_fields.length === 1 ? "Firma" : "Firme"}:{" "}
                  <span style={{ fontFamily: "var(--font-mono, monospace)", fontWeight: 600 }}>
                    {uploadResult.signature_fields.join(", ")}
                  </span>
                </span>
              )}
            </div>
          </div>

          {uploadResult.fields.length > 0 && (
            <div className="card" style={{ marginTop: "var(--space-4)" }}>
              <h2 className="section-title">Campi del documento</h2>
              <p style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)", marginBottom: "var(--space-4)" }}>
                Per ogni campo, scegli se compilarlo ora o delegarlo all'agente WhatsApp.
              </p>
              {uploadResult.fields
                .filter((f) => !new Set(uploadResult.signature_fields).has(f.name))
                .map((f) => (
                <FieldToggle
                  key={f.name}
                  fieldName={f.name}
                  label={enrichedMap.get(f.name)?.label}
                  description={enrichedMap.get(f.name)?.description}
                  mode={fields[f.name]?.mode ?? "agent"}
                  value={fields[f.name]?.value ?? ""}
                  onModeChange={(mode) => updateField(f.name, { mode })}
                  onValueChange={(value) => updateField(f.name, { value })}
                />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );

  /* ── Step 2 — Recipient ── */
  const renderRecipient = () => (
    <div className="card">
      <h2 className="section-title">Dati Destinatario</h2>
      <p style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)", marginBottom: "var(--space-4)" }}>
        Inserisci i dati del firmatario. Il numero WhatsApp verrà usato per contattarlo.
      </p>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Nome *</label>
          <input
            className="form-input"
            type="text"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            placeholder="Mario"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Cognome</label>
          <input
            className="form-input"
            type="text"
            value={recipientSurname}
            onChange={(e) => setRecipientSurname(e.target.value)}
            placeholder="Rossi"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Telefono (WhatsApp) *</label>
        <input
          className="form-input"
          type="tel"
          value={recipientPhone}
          onChange={(e) => setRecipientPhone(e.target.value)}
          placeholder="+39 333 1234567"
        />
        <span style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)", marginTop: 4, display: "block" }}>
          Inserisci il numero con prefisso internazionale (es. +39 per Italia). Senza prefisso verrà aggiunto +39.
        </span>
      </div>

      <div className="form-group">
        <label className="form-label">Azienda (opzionale)</label>
        <input
          className="form-input"
          type="text"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="Nome Azienda S.r.l."
        />
      </div>
    </div>
  );

  /* ── Step 3 — Review ── */
  /* Enriched field lookup */
  const enrichedMap = new Map<string, EnrichedField>();
  if (uploadResult?.enriched_fields) {
    for (const ef of uploadResult.enriched_fields) {
      enrichedMap.set(ef.field_name, ef);
    }
  }

  const selectedRecog = RECOGNITION_TYPES.find((r) => r.value === recogType);
  const prefilledCount = Object.values(fields).filter((f) => f.mode === "prefill").length;
  const agentCount = Object.values(fields).filter((f) => f.mode === "agent").length;

  const renderReview = () => (
    <div className="card">
      <h2 className="section-title">Riepilogo</h2>
      <p style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)", marginBottom: "var(--space-4)" }}>
        Verifica i dati prima di avviare la sessione di firma.
      </p>

      <div className="detail-grid" style={{ marginBottom: "var(--space-6)" }}>
        <div>
          <span className="detail-label">Riconoscimento</span>
          <span className="detail-value">{selectedRecog?.label || "—"}</span>
        </div>
        <div>
          <span className="detail-label">Documento</span>
          <span className="detail-value">{uploadResult?.filename || "—"}</span>
        </div>
        <div>
          <span className="detail-label">Campi compilati</span>
          <span className="detail-value">{prefilledCount} di {prefilledCount + agentCount}</span>
        </div>
        <div>
          <span className="detail-label">Delegati all'agente</span>
          <span className="detail-value">{agentCount}</span>
        </div>
      </div>

      <div style={{ borderTop: "1px solid var(--indaco-gray-200)", paddingTop: "var(--space-4)" }}>
        <h3 style={{ fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-semibold)", marginBottom: "var(--space-3)" }}>
          Destinatario
        </h3>
        <div className="detail-grid">
          <div>
            <span className="detail-label">Nome</span>
            <span className="detail-value">{recipientName} {recipientSurname}</span>
          </div>
          <div>
            <span className="detail-label">Telefono</span>
            <span className="detail-value">{recipientPhone}</span>
          </div>
          {companyName && (
            <div>
              <span className="detail-label">Azienda</span>
              <span className="detail-value">{companyName}</span>
            </div>
          )}
        </div>
      </div>

      {prefilledCount > 0 && (
        <div style={{ borderTop: "1px solid var(--indaco-gray-200)", paddingTop: "var(--space-4)", marginTop: "var(--space-4)" }}>
          <h3 style={{ fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-semibold)", marginBottom: "var(--space-3)" }}>
            Campi precompilati
          </h3>
          <div className="detail-grid">
            {Object.entries(fields)
              .filter(([, s]) => s.mode === "prefill")
              .map(([name, s]) => (
                <div key={name}>
                  <span className="detail-label">{enrichedMap.get(name)?.label || name}</span>
                  <span className="detail-value">{s.value || "—"}</span>
                </div>
              ))}
          </div>
        </div>
      )}

    </div>
  );

  const stepContent = [renderRecognition, renderDocument, renderRecipient, renderReview];

  if (retryLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-8)" }}>
        <span className="spinner spinner-lg" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="page-title">{isRetryMode ? "Riavvia Sessione" : "Nuovo Dossier"}</h1>
      <p className="page-subtitle">
        {isRetryMode
          ? "Verifica i dati e riavvia la sessione di firma. Puoi modificare qualsiasi campo tornando indietro."
          : "Configura il tipo di riconoscimento, carica il documento e inserisci i dati del destinatario."}
      </p>

      <Stepper current={step} />

      {stepContent[step]()}

      {/* Navigation */}
      <div className="wizard-nav">
        <div>
          {step > 0 && (
            <button type="button" className="btn btn-outline btn-lg" onClick={back}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
              Indietro
            </button>
          )}
        </div>
        <div>
          {step < 3 ? (
            <button type="button" className="btn btn-primary btn-lg" onClick={next} disabled={!canNext()}>
              Avanti
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-success btn-lg"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <><span className="spinner" /> {isRetryMode ? "Riavvio…" : "Invio in corso…"}</>
              ) : (
                <>
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{isRetryMode ? "refresh" : "send"}</span>
                  {isRetryMode ? "Riavvia Sessione" : "Avvia Sessione di Firma"}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Toasts */}
      {error && (
        <div className="toast toast-error" style={{ marginTop: "var(--space-3)" }}>
          <span className="material-symbols-outlined">error</span>
          {error}
        </div>
      )}
      {success && (
        <div className="toast toast-success" style={{ marginTop: "var(--space-3)" }}>
          <span className="material-symbols-outlined">check_circle</span>
          {success}
        </div>
      )}
    </div>
  );
}
